import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Captions, FileText, RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function useCues(transcript) {
  return useMemo(() => {
    const sentences = (transcript || "").replace(/\n+/g, " ").match(/[^.!?]+[.!?]+["']?|\S[^.!?]*$/g) || [];
    let t = 0;
    const cues = sentences.map((s) => { const d = Math.max(1.6, s.trim().split(/\s+/).length / 2.5); const c = { start: t, end: t + d, text: s.trim() }; t += d; return c; });
    return { cues, duration: t };
  }, [transcript]);
}

function AiPill() {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200" data-testid="ai-presenter-label">
      <Sparkles className="h-3.5 w-3.5" aria-hidden /> AI-generated presenter
    </span>
  );
}

function PreviewPlayer({ transcript, title, onEnded, captionsOn }) {
  const { cues, duration } = useCues(transcript);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const ended = useRef(false);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setT((v) => Math.min(duration, v + 0.25)), 250);
    return () => clearInterval(id);
  }, [playing, duration]);
  useEffect(() => {
    if (duration && t >= duration - 0.3 && !ended.current) { ended.current = true; setPlaying(false); onEnded?.(); }
  }, [t, duration, onEnded]);
  const cue = cues.find((c) => t >= c.start && t < c.end) || cues[cues.length - 1];
  return (
    <div className="relative aspect-video bg-gradient-to-br from-teal-900 via-teal-800 to-sky-900 text-white grain overflow-hidden" data-testid="preview-player">
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
        <span className="text-xs uppercase tracking-[0.18em] text-teal-100/90 font-semibold">{title}</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20" data-testid="video-placeholder-badge">Video coming soon · caption preview</span>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 sm:px-14 text-center">
        {!playing && t === 0 ? (
          <button onClick={() => setPlaying(true)} className="group flex flex-col items-center gap-4" data-testid="video-play-button" aria-label="Play welcome video">
            <span className="h-20 w-20 rounded-full bg-white text-teal-800 flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform"><Play className="h-9 w-9 ml-1" /></span>
            <span className="text-sm text-teal-50">Play · about {Math.round(duration / 60)} min</span>
          </button>
        ) : captionsOn ? (
          <p className="font-display text-lg sm:text-2xl lg:text-[1.7rem] leading-snug font-medium max-w-3xl" aria-live="polite" data-testid="preview-caption">{cue?.text}</p>
        ) : <p className="text-teal-100/70 text-sm">Captions off</p>}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/50 to-transparent flex items-center gap-3">
        <button onClick={() => (t >= duration ? (setT(0), (ended.current = false), setPlaying(true)) : setPlaying(!playing))} className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" aria-label={playing ? "Pause" : "Play"} data-testid="video-toggle-play">
          {t >= duration ? <RotateCcw className="h-4 w-4" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <input type="range" min={0} max={duration} step={0.25} value={t} onChange={(e) => setT(Number(e.target.value))} className="flex-1 accent-teal-300" aria-label="Seek" data-testid="video-seek-slider" />
        <span className="text-xs tabular-nums w-24 text-right">{fmt(t)} / {fmt(duration)}</span>
      </div>
    </div>
  );
}

function RealPlayer({ src, captionsSrc, poster, initialPortrait, onEnded, captionsOn }) {
  const ref = useRef(null);
  const fired = useRef(false);
  const [portrait, setPortrait] = useState(!!initialPortrait);
  const fire = () => { if (!fired.current) { fired.current = true; onEnded?.(); } };
  const check = () => { const v = ref.current; if (v && v.duration && v.duration - v.currentTime < 0.6) fire(); };
  useEffect(() => {
    const v = ref.current;
    if (v?.textTracks?.[0]) v.textTracks[0].mode = captionsOn ? "showing" : "hidden";
  }, [captionsOn]);
  return (
    <video ref={ref} className={portrait ? "block mx-auto h-[min(78vh,760px)] aspect-[9/16] max-w-full bg-slate-900" : "w-full aspect-video bg-slate-900"} controls playsInline preload="metadata" poster={poster || undefined}
      onLoadedMetadata={(e) => setPortrait(e.target.videoHeight > e.target.videoWidth)} onEnded={fire} onTimeUpdate={check} onSeeked={check} data-testid="course-video">
      <source src={src} />
      {captionsSrc && <track kind="captions" src={captionsSrc} srcLang="en" label="English" default />}
    </video>
  );
}

export function CoursePlayer({ video, src, captionsSrc, transcript, title, onEnded }) {
  const [captionsOn, setCaptionsOn] = useState(true);
  const [showTranscript, setShowTranscript] = useState(false);
  const realSrc = video?.external_url || (video?.has_video ? src : null);
  return (
    <div className="space-y-3" data-testid="course-player">
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg shadow-teal-900/5 bg-slate-900">
        {realSrc ? <RealPlayer src={realSrc} captionsSrc={video?.has_captions ? captionsSrc : null} poster={video?.has_poster ? src.replace(/\/video$/, "/poster") : null} initialPortrait={video?.portrait} onEnded={onEnded} captionsOn={captionsOn} />
          : <PreviewPlayer transcript={transcript} title={title} onEnded={onEnded} captionsOn={captionsOn} />}
      </div>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <AiPill />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" aria-pressed={captionsOn} onClick={() => setCaptionsOn(!captionsOn)} data-testid="captions-toggle">
            <Captions className="h-4 w-4 mr-1.5" /> Captions {captionsOn ? "on" : "off"}
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" aria-expanded={showTranscript} onClick={() => setShowTranscript(!showTranscript)} data-testid="transcript-toggle">
            <FileText className="h-4 w-4 mr-1.5" /> {showTranscript ? "Hide" : "Show"} transcript
          </Button>
        </div>
      </div>
      {showTranscript && (
        <div className="max-h-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-700 leading-relaxed space-y-3" data-testid="transcript-panel">
          {(transcript || "").split(/\n\n+/).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
    </div>
  );
}
